import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Trophy, Calendar, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AchievementCarousel = () => {
  const [achievements, setAchievements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/achievements`);
        if (response.ok) {
          const data = await response.json();
          setAchievements(data.achievements || []);
        }
      } catch (err) {
        console.error('Failed to fetch achievements:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAchievements();
  }, []);

  const nextSlide = useCallback(() => {
    if (achievements.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % achievements.length);
    }
  }, [achievements.length]);

  const prevSlide = () => {
    if (achievements.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + achievements.length) % achievements.length);
    }
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || achievements.length <= 1) return;
    
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, achievements.length, nextSlide]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-br from-green-900/20 to-blue-900/20 rounded-2xl">
        <div className="animate-spin w-8 h-8 border-4 border-lime-400 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (achievements.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center h-64 bg-gradient-to-br from-green-900/20 to-blue-900/20 rounded-2xl border border-white/10"
        data-testid="no-achievements"
      >
        <Trophy className="w-12 h-12 text-lime-400 mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">CDA Player Achievements</h3>
        <p className="text-gray-400 text-sm">Coming soon - celebrating our players' success!</p>
      </div>
    );
  }

  const currentAchievement = achievements[currentIndex];

  return (
    <div 
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/80 to-blue-900/80 backdrop-blur-sm border border-white/10"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      data-testid="achievement-carousel"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/60 to-transparent p-4">
        <div className="flex items-center gap-2 text-lime-400">
          <Trophy className="w-5 h-5" />
          <span className="text-sm font-semibold uppercase tracking-wider">Player of the Month</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative h-80 md:h-96">
        <div 
          className="absolute inset-0 flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {achievements.map((achievement, index) => (
            <div 
              key={achievement.achievement_id || index}
              className="flex-shrink-0 w-full h-full flex items-center"
            >
              <div className="w-full h-full flex flex-col md:flex-row items-center p-6 md:p-10 gap-6">
                {/* Image */}
                <div className="relative w-32 h-32 md:w-48 md:h-48 flex-shrink-0">
                  {achievement.image_url ? (
                    <img 
                      src={achievement.image_url.startsWith('/api') ? `${API_URL}${achievement.image_url}` : achievement.image_url}
                      alt={achievement.player_name}
                      className="w-full h-full object-cover rounded-full border-4 border-lime-400 shadow-lg shadow-lime-400/20"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full border-4 border-lime-400 bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                      <span className="text-4xl md:text-6xl font-bold text-white">
                        {achievement.player_name?.charAt(0) || '?'}
                      </span>
                    </div>
                  )}
                  <div className="absolute -bottom-2 -right-2 bg-yellow-500 rounded-full p-2">
                    <Star className="w-5 h-5 text-white fill-white" />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {achievement.player_name}
                  </h3>
                  <div className="inline-flex items-center gap-2 bg-lime-500/20 text-lime-400 px-4 py-2 rounded-full mb-4">
                    <Trophy className="w-4 h-4" />
                    <span className="font-semibold">{achievement.achievement_title}</span>
                  </div>
                  {achievement.description && (
                    <p className="text-gray-300 text-sm md:text-base mb-4 max-w-md">
                      {achievement.description}
                    </p>
                  )}
                  <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{achievement.achievement_date}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {achievements.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots */}
      {achievements.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {achievements.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-lime-400 w-6' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AchievementCarousel;
